import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';

interface ComboboxFormFieldProps {
  selectedValues: string[];
  options: { value: string; label: string }[];
  placeholder?: string;
  hasError?: boolean;
  modal?: boolean;
  canEdit?: boolean;
  onChange: (selectedValues: string[]) => void;
}

export const Combobox = ({
  selectedValues,
  options,
  placeholder,
  canEdit = true,
  modal = false,
  hasError,
  onChange,
}: ComboboxFormFieldProps) => {
  const currentValue = selectedValues ?? [];

  const toggleValue = (val: string) => {
    if (currentValue.includes(val)) {
      onChange(currentValue.filter((v) => v !== val));
    } else {
      onChange([...currentValue, val]);
    }
  };

  return (
    <Popover modal={modal}>
      <PopoverTrigger className="w-full" asChild disabled={!canEdit}>
        <Button
          variant="outline"
          role="combobox"
          className={`h-auto min-h-9 w-full justify-between py-1 font-normal ${hasError && 'border-destructive'}`}
        >
          <div className="flex flex-wrap gap-1">
            {currentValue.length > 0 ? (
              currentValue.sort().map((val, index) => (
                <Badge variant={'secondary'} key={index}>
                  {options.find((option) => option.value === val)?.label ??
                    options.find((option) => option.value === val)?.value}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    toggleValue(currentValue);
                  }}
                >
                  {option.label ?? option.value}
                  <Check
                    className={cn(
                      'ml-auto',
                      currentValue.includes(option.value)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
